/**
 * Centralized tier type and helpers.
 *
 * Business rules:
 * - A rooftop is either 'lite' or 'standard' (stored on customers.tier)
 * - A user can have access to rooftops of mixed tiers
 * - A user's "effective tiers" = the set of tiers from their linked rooftops
 */

export type Tier = 'lite' | 'standard';

/** All known tier values for type guards */
const TIER_VALUES = new Set<string>(['lite', 'standard']);

export function isLiteTier(tier: string | null | undefined): boolean {
  return tier === 'lite';
}

export function isStandardTier(tier: string | null | undefined): boolean {
  return tier === 'standard';
}

export function isValidTier(tier: string | null | undefined): tier is Tier {
  return !!tier && TIER_VALUES.has(tier);
}

// ── Object-level helpers ──

/** Check if a rooftop (customer) is lite tier */
export function isRooftopLite(rooftop: { tier?: string | null } | null | undefined): boolean {
  return isLiteTier(rooftop?.tier);
}

/** Check if a rooftop (customer) is standard tier */
export function isRooftopStandard(rooftop: { tier?: string | null } | null | undefined): boolean {
  return isStandardTier(rooftop?.tier);
}

/** Check if a move is lite (has the lite boolean flag set) */
export function isMoveLite(move: { lite?: boolean | null } | null | undefined): boolean {
  return !!move?.lite;
}

// ── User tier context ──

export interface UserTierInfo {
  /** All tiers this user has access to */
  tiers: Set<Tier>;
  /** Has at least one lite rooftop */
  hasLite: boolean;
  /** Has at least one standard rooftop */
  hasStandard: boolean;
  /** Has both lite and standard rooftops */
  isMixed: boolean;
  /** Only has lite rooftops (no standard) */
  isLiteOnly: boolean;
  /** Only has standard rooftops (no lite) */
  isStandardOnly: boolean;
}

/**
 * Derive tier info from a user's linked customers.
 * Accepts any array of objects with a customer that has a tier field.
 */
export function getUserTierInfo(
  usertocustomers: Array<{ customer?: { tier?: string | null } | null }> | null | undefined,
): UserTierInfo {
  const tiers = new Set<Tier>();

  if (usertocustomers) {
    for (const utc of usertocustomers) {
      const t = utc.customer?.tier;
      if (t && isValidTier(t)) {
        tiers.add(t);
      }
    }
  }

  const hasLite = tiers.has('lite');
  const hasStandard = tiers.has('standard');

  return {
    tiers,
    hasLite,
    hasStandard,
    isMixed: hasLite && hasStandard,
    isLiteOnly: hasLite && !hasStandard,
    isStandardOnly: hasStandard && !hasLite,
  };
}
